import { enumField, ModelInstance, ToFrontHook, ToBackHook, BaseModel, DateTimeField } from './util';
import { GameUser } from './game-user';
import { GameLogBase } from './game-log-base';

export enum BasketballGameLogTypes {
  enter_game = 1,
  exit_game,
  remove_game,
  two_point_made,
  three_point_made,
  free_throw_made,
  two_point_attempt,
  three_point_attempt,
  free_throw_attempt,
  assist,
  block,
  rebound,
  offensive_rebound,
  defensive_rebound,
  steal,
  turnover,
  personal_foul,
  technical_foul,
  unsportsmanlike_foul,
  disqualification_foul,
  one_point_attempt,
  one_point_made,
  timeout,
  team_rebound,
  four_point_attempt,
  four_point_made,
  dunk_attempt,
  dunk_made,
  shootout,
  shootout_won,
  shootout_lost,
  moneyball_free_throw_attempt,
  moneyball_free_throw_made,
}

export enum BasketballSubLogTypes {
  // Miss, Made
  jump_shot = 1,
  step_back_jump_shot,
  floating_jump_shot,
  fade_away_jump_shot,
  pull_up_jump_shot,
  turnaround_jump_shot,
  dunk,
  layup,
  driving_layup,
  alley_oop,
  hook_shot,

  // foul
  offensive_foul,
  shot_foul,
  mutual_foul,

  // Turnover
  bad_pass,
  ball_handling,
  travel,
  double_dribble,
  out_of_bounds,
  three_seconds,
  five_seconds,
  eight_seconds,
  twenty_four_seconds,
  back_court,
  offensive_goal_tending,
  other_turnover,
}

export enum BasketballCourtZones {
  paint = 1,
  left_short_corner,
  right_short_corner,
  left_middle_post,
  right_middle_post,
  high_post,
  left_corner,
  right_corner,
  left_wing,
  right_wing,
  top,
}

export enum BasketballCourtSides {
  front = 1,
  back,
}

export enum BasketballPenaltyTypes {
  one_shot = 1,
  two_shots,
  three_shots,
  possession,
  one_shot_and_possession,
  two_shots_and_possession,
  three_shots_and_possession,
}

@ModelInstance({
  mappingFields: {
    id: 'id',
    unique_id: 'uniqueId',
    game_id: 'gameId',
    game_user_id: 'gameUserId',
    team_id: 'teamId',
    log_type: 'logType',
    sub_log_type: 'subLogType',
    datetime: 'datetime',
    time: 'time',
    period: 'period',
    active: 'active',
    is_highlight: 'isHighlight',
    zone: 'zone',
    court_side: 'courtSide',
    foul_on_game_user_id: 'foulOnGameUserId',
    penalty_type: 'penaltyType',
    fast_break: 'fastBreak',
    is_coach: 'isCoach',
    is_bench: 'isBench',
    group: 'group',
    marked: 'marked',
    is_additional_point: 'isAdditionalPoint',
    coords: 'coords',
    shootout_points: 'shootoutPoints',
  },
  relation: {
    datetime: DateTimeField,
    logType: enumField(BasketballGameLogTypes),
    subLogType: enumField(BasketballSubLogTypes),
    penaltyType: enumField(BasketballPenaltyTypes),
    zone: enumField(BasketballCourtZones),
    courtSide: enumField(BasketballCourtSides),
  }
})
export class BasketballGameLog extends BaseModel implements GameLogBase {
  id: number;
  uniqueId: string;
  gameId: number;
  gameUserId: number;
  gameUser: GameUser;
  teamId: number;
  logType: BasketballGameLogTypes;
  subLogType: BasketballSubLogTypes;
  datetime: Date;
  time: number;
  period: number;
  active = true;
  combined: BasketballGameLog;
  teamScore: number;
  competitorTeamScore: number;
  isHighlight: boolean;
  zone: BasketballCourtZones;
  courtSide: BasketballCourtSides;
  penaltyType: BasketballPenaltyTypes;
  foulOnGameUserId: number;
  foulOnGameUser: GameUser;
  fastBreak: boolean;
  isCoach: boolean;
  isBench: boolean;
  group: string;
  marked: boolean;
  isAdditionalPoint: boolean;
  coords: [number, number];
  shootoutPoints: number;

  isPointsType() {
    return [
      BasketballGameLogTypes.two_point_made,
      BasketballGameLogTypes.three_point_made,
      BasketballGameLogTypes.free_throw_made,
      BasketballGameLogTypes.one_point_made,
      BasketballGameLogTypes.four_point_made,
      BasketballGameLogTypes.dunk_made,
      BasketballGameLogTypes.shootout_won,
      BasketballGameLogTypes.moneyball_free_throw_made,
    ].indexOf(this.logType) > -1;
  }

  isReboundType() {
    return [
      BasketballGameLogTypes.rebound,
      BasketballGameLogTypes.offensive_rebound,
      BasketballGameLogTypes.defensive_rebound,
      BasketballGameLogTypes.team_rebound,
    ].indexOf(this.logType) > -1;
  }

  isFoulType() {
    return [
      BasketballGameLogTypes.personal_foul,
      BasketballGameLogTypes.technical_foul,
      BasketballGameLogTypes.unsportsmanlike_foul,
      BasketballGameLogTypes.disqualification_foul,
    ].indexOf(this.logType) > -1;
  }

  getScore() {
    const scoreMapping = {
      [BasketballGameLogTypes.one_point_made]: this.isAdditionalPoint ? 2 : 1,
      [BasketballGameLogTypes.free_throw_made]: 1,
      [BasketballGameLogTypes.two_point_made]: this.isAdditionalPoint ? 3 : 2,
      [BasketballGameLogTypes.three_point_made]: this.isAdditionalPoint ? 4 : 3,
      [BasketballGameLogTypes.four_point_made]: this.isAdditionalPoint ? 5 : 4,
      [BasketballGameLogTypes.dunk_made]: this.isAdditionalPoint ? 4 : 3,
      [BasketballGameLogTypes.moneyball_free_throw_made]: 2,
    };
    if (this.logType === BasketballGameLogTypes.shootout_won) {
      return this.shootoutPoints;
    }
    return scoreMapping[this.logType] || 0;
  }

  compare(model: BasketballGameLog): number {
    const orderFields = ['period', 'time', 'datetime', 'id'];
    for (const field of orderFields) {
      if (this[field] < model[field]) {
        return 1;
      }
      if (this[field] > model[field]) {
        return -1;
      }
    }
    return 0;
  }

  get timeFormatted(): string {
    const minutes = Math.floor(this.time / 60);
    const seconds = this.time - minutes * 60;
    return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  @ToFrontHook
  static toFront(value: any): any {}

  @ToBackHook
  static toBack(value: any): any {}
}
